import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { fetchIntegrations } from "../../../api/queries";
import { useStore } from "../../../contexts/StoreContext";
import Drawer from "../../../blocks/drawer";
import { getMarketingTimeZone } from "../../../utils/marketingTimeZone";
import { timeZoneLabel } from "../../../utils/timeZones";
import { zonedWallTimeToUtcIso } from "../../../utils/zonedTime";

const MessageModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  isEditing,
  passes,
  phoneNumbers,
  emailAddresses,
  isLoadingPhoneNumbers,
  isLoadingEmailAddresses,
  t,
  handleMessageTypeChange,
  error,
  attemptedType,
}) => {
  const { selectedStore } = useStore();
  const storeTimeZone = selectedStore?.time_zone;
  const { timeZone, fromStore } = useMemo(
    () => getMarketingTimeZone({ time_zone: storeTimeZone }),
    [storeTimeZone],
  );
  // The scheduled_at value last rejected as unreadable. The error shows only
  // while the field still holds it, so editing the time clears it. The drawer
  // stays mounted between sessions, so the value is also dropped whenever it
  // opens or closes; otherwise a later session (a new message, or another one
  // opened for edit) showing the same value would inherit the error before any
  // submit. Reset during render, not in an effect, so the stale error is never
  // painted for a frame.
  const [rejectedScheduledAt, setRejectedScheduledAt] = useState(null);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setRejectedScheduledAt(null);
  }
  const scheduledAtError =
    rejectedScheduledAt !== null &&
    rejectedScheduledAt === formData.scheduled_at
      ? t("marketing.form.invalidScheduledAt")
      : "";
  const [integrations, setIntegrations] = useState([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);

  // Fetch integrations when component mounts or message type changes
  useEffect(() => {
    const loadIntegrations = async () => {
      if (!selectedStore?.id) return;

      setIsLoadingIntegrations(true);
      const data = await fetchIntegrations(selectedStore.id);
      setIntegrations(data || []);
      setIsLoadingIntegrations(false);
    };

    loadIntegrations();
  }, [selectedStore?.id]);

  // Custom close handler that clears error states
  const handleClose = () => {
    onClose();
  };

  // Handle form submission: the entered wall time is read in the marketing
  // time zone (store's, else browser's) and sent as an ISO UTC instant.
  const handleSubmit = (e) => {
    e.preventDefault();

    const scheduledAtUtc = zonedWallTimeToUtcIso(
      formData.scheduled_at,
      timeZone,
    );
    // A value we can't read as a wall time must not reach the API raw — it
    // would be parsed there in some other zone, or not at all.
    if (formData.scheduled_at && !scheduledAtUtc) {
      setRejectedScheduledAt(formData.scheduled_at);
      return;
    }
    if (scheduledAtUtc) {
      const updatedFormData = {
        ...formData,
        integration_id: formData.integration_id,
        scheduled_at: scheduledAtUtc,
      };

      // Pass the updated form data to the parent's onSubmit
      onSubmit({
        ...e,
        preventDefault: () => {}, // We already called preventDefault
        target: e.target,
        updatedFormData,
      });
    } else {
      onSubmit(e);
    }
  };

  // Filter integrations based on message type
  const getFilteredIntegrations = () => {
    if (!formData.message_type || !integrations?.length) return [];

    const tagToMatch = formData.message_type === "sms" ? "sms" : "email";

    return integrations.filter(
      (integration) =>
        integration.tags && integration.tags.includes(tagToMatch),
    );
  };

  // Get filtered integrations based on message type
  const filteredIntegrations = getFilteredIntegrations();

  return (
    <Drawer
      open={isOpen}
      setOpen={onClose}
      header={
        <>{isEditing ? t("marketing.edit.title") : t("marketing.new.title")}</>
      }
      onSubmit={handleSubmit}
      submitText={isEditing ? t("common.save") : t("common.create")}
      cancelText={t("common.cancel")}
      onCancel={handleClose}
    >
      <div className="px-4 py-6 sm:px-6">
        <div className="space-y-6">
          {/* Pass Selection */}
          <div>
            <label
              htmlFor="pass"
              className="block text-sm font-medium text-gray-700"
            >
              {t("marketing.form.pass")}
            </label>
            <select
              id="pass"
              name="pass_id"
              value={formData.pass_id}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  pass_id: e.target.value,
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">{t("marketing.form.selectPass")}</option>
              <option value="all">{t("marketing.filter.allPasses")}</option>
              {passes.map((pass) => (
                <option key={pass.id} value={pass.id}>
                  {pass.pass.name} - {pass.pass.type}
                </option>
              ))}
            </select>
          </div>

          {/* Message Type Selection */}
          <div>
            <label
              htmlFor="message_type"
              className="block text-sm font-medium text-gray-700"
            >
              {t("marketing.form.messageType")}
            </label>
            <select
              id="message_type"
              name="message_type"
              value={formData.message_type}
              onChange={handleMessageTypeChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            >
              <option value="">{t("marketing.form.selectMessageType")}</option>
              <option value="sms">{t("marketing.kinds.sms")}</option>
              <option value="email">{t("marketing.kinds.email")}</option>
              <option value="push">{t("marketing.kinds.push")}</option>
            </select>
            {error && (
              <div className="mt-2 text-sm text-red-600">
                {error}{" "}
                <Link
                  to={
                    attemptedType === "email"
                      ? "/integrations?tag=email"
                      : "/integrations?tag=sms"
                  }
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  {attemptedType === "email"
                    ? t("marketing.errors.connectEmail")
                    : t("marketing.errors.connectSms")}
                </Link>
              </div>
            )}
          </div>

          {/* Provider Selection - Only show for SMS and email */}
          {(formData.message_type === "sms" ||
            formData.message_type === "email") && (
            <div>
              <label
                htmlFor="integration_id"
                className="block text-sm font-medium text-gray-700"
              >
                {t("marketing.form.provider")}
              </label>
              <select
                id="integration_id"
                name="integration_id"
                value={formData.integration_id || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    integration_id: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">{t("marketing.form.selectProvider")}</option>
                {isLoadingIntegrations ? (
                  <option disabled>{t("common.loading")}</option>
                ) : (
                  filteredIntegrations.map((integration) => (
                    <option key={integration.id} value={integration.id}>
                      {integration.integration_type.charAt(0).toUpperCase() +
                        integration.integration_type.slice(1)}
                    </option>
                  ))
                )}
              </select>
              {isLoadingIntegrations && (
                <div className="mt-2 text-sm text-gray-500">
                  {t("common.loading")}
                </div>
              )}
              {!isLoadingIntegrations && filteredIntegrations.length === 0 && (
                <div className="mt-2 text-sm text-red-600">
                  {t("marketing.errors.noProviders")}{" "}
                  <Link
                    to={
                      formData.message_type === "email"
                        ? "/integrations?tag=email"
                        : "/integrations?tag=sms"
                    }
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    {t("marketing.errors.connectProvider")}
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* From Selection - Only show for SMS and email when a provider is selected */}
          {(formData.message_type === "sms" ||
            formData.message_type === "email") &&
            formData.integration_id && (
              <div>
                <label
                  htmlFor="from"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("marketing.form.from")}
                </label>
                <select
                  id="from"
                  name="from"
                  value={formData.from}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                >
                  <option value="">{t("marketing.form.select")}</option>
                  {formData.message_type === "sms" &&
                    phoneNumbers &&
                    (Array.isArray(phoneNumbers)
                      ? phoneNumbers
                      : phoneNumbers?.phone_numbers || []
                    ).map((phoneNumber, index) => (
                      <option key={`phone-${index}`} value={phoneNumber}>
                        {phoneNumber}
                      </option>
                    ))}
                  {formData.message_type === "email" &&
                    emailAddresses &&
                    (Array.isArray(emailAddresses)
                      ? emailAddresses
                      : emailAddresses?.email_addresses || []
                    ).map((emailAddress, index) => (
                      <option key={`email-${index}`} value={emailAddress}>
                        {emailAddress}
                      </option>
                    ))}
                </select>
                {formData.message_type === "sms" && isLoadingPhoneNumbers && (
                  <div className="mt-2 text-sm text-gray-500">
                    {t("common.loading")}
                  </div>
                )}
                {formData.message_type === "email" &&
                  isLoadingEmailAddresses && (
                    <div className="mt-2 text-sm text-gray-500">
                      {t("common.loading")}
                    </div>
                  )}
              </div>
            )}

          {/* Email Subject (only for email when provider is selected) */}
          {formData.message_type === "email" && formData.integration_id && (
            <div>
              <label
                htmlFor="subject"
                className="block text-sm font-medium text-gray-700"
              >
                {t("marketing.form.emailSubject")}
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    subject: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required={formData.message_type === "email"}
              />
            </div>
          )}

          {/* Scheduled At */}
          <div>
            <label
              htmlFor="scheduled_at"
              className="block text-sm font-medium text-gray-700"
            >
              {t("marketing.form.scheduledAt")}
            </label>
            <input
              type="datetime-local"
              id="scheduled_at"
              name="scheduled_at"
              value={formData.scheduled_at}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  scheduled_at: e.target.value,
                }))
              }
              className={`mt-1 block w-full rounded-md shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                scheduledAtError ? "border-red-300" : "border-gray-300"
              }`}
              aria-invalid={scheduledAtError ? true : undefined}
              required
            />
            {scheduledAtError && (
              <p className="mt-2 text-sm text-red-600">{scheduledAtError}</p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              {fromStore
                ? t("marketing.form.timeZoneNote", {
                    timeZone: timeZoneLabel(timeZone),
                  })
                : t("marketing.form.timeZoneBrowserFallback", {
                    timeZone: timeZoneLabel(timeZone),
                  })}
            </p>
          </div>

          {/* Message Content */}
          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700"
            >
              {t("marketing.form.message")}
            </label>
            {formData.message_type === "email" && formData.integration_id ? (
              <div className="mt-1">
                <RichTextEditor
                  content={formData.message}
                  onChange={(content) =>
                    setFormData((prev) => ({
                      ...prev,
                      message: content,
                    }))
                  }
                />
              </div>
            ) : (
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }))
                }
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

MessageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  passes: PropTypes.array.isRequired,
  phoneNumbers: PropTypes.object,
  emailAddresses: PropTypes.object,
  isLoadingPhoneNumbers: PropTypes.bool,
  isLoadingEmailAddresses: PropTypes.bool,
  t: PropTypes.func.isRequired,
  handleMessageTypeChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  attemptedType: PropTypes.string,
};

export default React.memo(MessageModal);
